import React, { useState } from 'react';
import { Link } from 'react-router-dom';

/** Fallback until transparent light/dark assets are supplied. */
const LOGO_FALLBACK = '/brand/kinvera-logo.png';
const LOGO_LIGHT = '/brand/kinvera-logo-light.png';
const LOGO_DARK = '/brand/kinvera-logo-dark.png';

/** Full lockup (mark + wordmark in the PNG). Height drives size; width follows aspect ratio. */
const sizeMap = {
  sm: 36,
  md: 48,
  lg: 64,
  xl: 88,
};

type BrandLogoProps = {
  to?: string | null;
  size?: keyof typeof sizeMap;
  /** Extra text next to the image — off by default; the PNG already includes “Kinvera”. */
  showWordmark?: boolean;
  className?: string;
  /** Use dark-surface logo when true (Studio / dark headers). */
  onDark?: boolean;
};

export const BrandLogo: React.FC<BrandLogoProps> = ({
  to = '/',
  size = 'md',
  showWordmark = false,
  className = '',
  onDark = false,
}) => {
  const preferred = onDark ? LOGO_DARK : LOGO_LIGHT;
  const [src, setSrc] = useState(preferred);
  const [imgFailed, setImgFailed] = useState(false);
  const height = sizeMap[size];

  // Reset source when onDark changes (e.g. navigating marketing ↔ studio)
  React.useEffect(() => {
    setSrc(onDark ? LOGO_DARK : LOGO_LIGHT);
    setImgFailed(false);
  }, [onDark]);

  const onError = () => {
    if (src !== LOGO_FALLBACK) {
      setSrc(LOGO_FALLBACK);
      return;
    }
    setImgFailed(true);
  };

  const inner = (
    <span className={`kin-brand-logo ${onDark ? 'kin-brand-logo-on-dark' : ''} ${className}`.trim()}>
      {!imgFailed ? (
        <img
          src={src}
          alt="Kinvera"
          height={height}
          className="kin-brand-logo-mark"
          style={{ height, width: 'auto' }}
          onError={onError}
        />
      ) : (
        <span
          className="kin-brand-logo-fallback"
          style={{ height, minWidth: Math.round(height * 3.2) }}
          aria-hidden
        >
          Kinvera
        </span>
      )}
      {showWordmark && <span className="kin-brand-logo-word">Kinvera</span>}
    </span>
  );

  if (to === null || to === '') {
    return inner;
  }
  return (
    <Link to={to} className="kin-brand-logo-link" aria-label="Kinvera home">
      {inner}
    </Link>
  );
};
